/**
 * GM-only dialog for editing hex settings.
 * Allows setting the hex name and custom owner display name.
 */

import { useState, useCallback } from 'react';

interface Props {
    hexId: number;
    hexName: string;
    ownerName: string | null;
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, ownerName: string | null) => void;
}

export function HexSettingsDialog({ hexId, hexName, ownerName, isOpen, onClose, onSave }: Props) {
    const [name, setName] = useState(hexName);
    const [customOwnerName, setCustomOwnerName] = useState(ownerName || '');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSaving(true);

        try {
            const response = await fetch(`/hex/${hexId}/settings`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({
                    name: name.trim(),
                    owner_name: customOwnerName.trim() || null,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update settings');
            }

            const data = await response.json();
            onSave(data.hex.name, data.hex.ownerName);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update settings');
        } finally {
            setIsSaving(false);
        }
    }, [hexId, name, customOwnerName, onSave, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70"
                onClick={onClose}
            />

            {/* Dialog */}
            <div className="relative bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-slate-700">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-700">
                    <h2 className="text-lg font-semibold text-white">Hex Settings</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {error && (
                        <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Hex Name */}
                    <div>
                        <label htmlFor="hex-name" className="block text-sm font-medium text-gray-300 mb-1">
                            Hex Name
                        </label>
                        <input
                            id="hex-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter hex name"
                            required
                            maxLength={100}
                        />
                    </div>

                    {/* Custom Owner Name */}
                    <div>
                        <label htmlFor="owner-name" className="block text-sm font-medium text-gray-300 mb-1">
                            Custom Owner Name
                        </label>
                        <input
                            id="owner-name"
                            type="text"
                            value={customOwnerName}
                            onChange={(e) => setCustomOwnerName(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Leave empty to use actual owner name"
                            maxLength={100}
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            For enclave hexes: displays this name instead of the actual owner's username on the map.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving || !name.trim()}
                            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
